// Additional hook for rendering the table view in file explorer (right side)

import { useEffect, useState } from "react";
import { useHideObjectsMutation } from "../backend/hooks/contextMenu/fileOperations/mutationHideObjects";
import { useUpdateActionStatusMutation } from "../backend/hooks/action/mutationUpdateActionStatus";
import { useDownloadLinksQuery } from "../backend/hooks/importExportMenu/queryGetDownloadLinks";
import {
  ActionStatus,
  ExtendedRepositoryObject,
  PmxActivity,
  RepositoryObject,
  RepositoryObjectFolder
} from "../backend/types";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import {
  buildSortFilterRequestParams,
  isValidFilter
} from "../helpers/urlHelper";

// Hook used for upload and download of files
export function useTableViewHook(
  callRepositoryObjects: Function,
  folderInfo: RepositoryObjectFolder,
  currentSelectedPath: string,
  currentSelectedRepoId: string,
  activityData: PmxActivity,
  refetchRepositoryObjects: Function,
  tableMenuWasOpenOnPath: string,
  createFolderMutation: Function,
  setTableMenuWasOpenOnPath: Function,
  setIsActionOfFilesVisible: Function,
  setIsTracebilityReportVisible: Function,
  setIsEditActionVisible: Function,
  tableViewFiles: RepositoryObject[],
  areRepoObjectsLoading: boolean
) {
  const [anchorElFolder, setAnchorElFolder] = useState<null | HTMLElement>(
    null
  );
  const [isDownloadExternalLinkVisible, setIsDownloadExternalLinkVisible] =
    useState(false);
  const [hideComment, setHideComment] = useState("");
  const [anchorElFile, setAnchorElFile] = useState<null | HTMLElement>(null);
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);
  const [isCompressModalOpen, setIsCompressModalOpen] = useState(false);
  const [isUncompressModalOpen, setIsUncompressModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isCopyMoveModalOpen, setIsCopyMoveModalOpen] = useState(false);

  const [folderName, setFolderName] = useState("");
  const [isFolderNameError, setIsFolderNameError] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [currentCommentsForSelectedFile, setCurrentCommentsForSelectedFile] =
    useState<null | any[]>([]);
  const [isFileContextMenuOpen, setIsFileContextMenuOpen] = useState(false);
  const [copyOrMoveAction, setCopyOrMoveAction] = useState("'");
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    undefined
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);

  const { updateActionStatus } = useUpdateActionStatusMutation();

  const {
    getDownloadLinkForFiles,
    errorDownload: error,
    downloadUrls
  } = useDownloadLinksQuery();

  const totalPages = Math.ceil((folderInfo?.count ?? 0) / pageSize);

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(0);
    }
  }, [folderInfo?.count, page, totalPages, setPage]);

  // Track last request params to avoid duplicate requests
  const [lastRequestParams, setLastRequestParams] = useState<any>(null);

  // Unified effect to handle all relevant changes and avoid duplicate requests
  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }

    // If filter is set and valid, use it; otherwise, ignore filter
    const useFilter =
      filterModel &&
      (filterModel.items.length === 0 ||
        filterModel.items.every(isValidFilter));

    const params = buildSortFilterRequestParams({
      repoId: currentSelectedRepoId,
      location: currentSelectedPath,
      pageNumber: page + 1,
      pageSize,
      sortModel,
      filterModel: useFilter ? filterModel : undefined
    });

    let shouldResetPage = false;

    if (lastRequestParams) {
      // Compare sort and filter models only
      const lastSort = JSON.stringify(lastRequestParams.sort || []);
      const currSort = JSON.stringify(params.sort || []);
      const lastFilter = JSON.stringify(lastRequestParams.filter || {});
      const currFilter = JSON.stringify(params.filter || {});

      if (lastSort !== currSort || lastFilter !== currFilter) {
        shouldResetPage = true;
      }
    }

    if (shouldResetPage) {
      params.pageNumber = 1;
      setPage(0); // Also reset local page state
    }

    callRepositoryObjects({ variables: params });
    setLastRequestParams(params);
  }, [sortModel, page, pageSize, filterModel]);

  useEffect(() => {
    if (typeof downloadUrls !== "undefined") {
      if (downloadUrls.getDownloadLinks.actionStatus === ActionStatus.Pending) {
        try {
          downloadUrls?.getDownloadLinks?.presignedUrls.forEach((url: any) => {
            window.open(url.url);
          });
          updateActionStatus({
            variables: {
              actionId: downloadUrls.getDownloadLinks.actionId,
              actionStatus: ActionStatus.Success
            }
          });
        } catch (err: any) {
          updateActionStatus({
            variables: {
              actionId: downloadUrls.getDownloadLinks.actionId,
              actionStatus: ActionStatus.Error,
              actionStatusDescription: err.toString()
            }
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadUrls]);

  const handleSelectMultipleRows = (e: any) => {
    !isCopyMoveModalOpen && setSelectedRows(e);
  };

  const downloadMultipleFiles = () => {
    getDownloadLinkForFiles({
      variables: {
        absolutePaths:
          selectedRows.length > 0 ? selectedRows : [tableMenuWasOpenOnPath],
        activityId: activityData.id,
        versionIds: [],
        repoId: currentSelectedRepoId
      }
    });
    handleCloseFileMenu();
  };

  const createFolder = () => {
    if (
      extendTableRecordDetails?.some(
        (item: ExtendedRepositoryObject) =>
          item.absolutePath.endsWith("/") && item.name === folderName
      )
    ) {
      setIsFolderNameError(true);
      return;
    }

    setIsFolderNameError(false);
    createFolderMutation(folderName);
    setFolderName("");
  };

  const closeHideModal = () => {
    setIsHideModalOpen(false);
    setHideComment("");
  };

  const handleClickFolder = (
    event: React.MouseEvent<HTMLButtonElement>,
    absolutePath: string
  ) => {
    if (!selectedRows.includes(absolutePath)) {
      setSelectedRows([]);
    }
    setAnchorElFolder(event.currentTarget);
    setTableMenuWasOpenOnPath(absolutePath);
  };

  const handleCloseFolder = () => {
    setAnchorElFolder(null);
  };

  const handleCloseCommentMenu = () => {
    setCurrentCommentsForSelectedFile([]);
    handleCloseFileMenu();
  };

  const handleCloseFileMenu = () => {
    setAnchorElFile(null);
    setIsFileContextMenuOpen(false);
    setTableMenuWasOpenOnPath("");
  };

  const handleOpenProperties = () => {
    setIsActionOfFilesVisible(true);
    setIsFileContextMenuOpen(false);
    setAnchorElFile(null);
  };

  const handleOpenEditAction = () => {
    setIsEditActionVisible(true);
    setIsFileContextMenuOpen(false);
    setAnchorElFile(null);
  };

  const handleOpenTracebilityReport = () => {
    setIsTracebilityReportVisible(true);
    setIsFileContextMenuOpen(false);
    setAnchorElFile(null);
  };

  const handleOpenExternalDownloadLink = () => {
    setIsDownloadExternalLinkVisible(true);
    setIsFileContextMenuOpen(false);
    setAnchorElFile(null);
  };

  const handleClickFile = (
    event: React.MouseEvent<HTMLButtonElement>,
    absolutePath: string,
    openContextMenu: boolean = true
  ) => {
    if (!selectedRows.includes(absolutePath)) {
      setSelectedRows([]);
    }
    if (openContextMenu) {
      setAnchorElFile(event.currentTarget);
      setIsFileContextMenuOpen(true);
    }
    setTableMenuWasOpenOnPath(absolutePath);
  };

  const handleClickComment = (
    event: React.MouseEvent<HTMLButtonElement>,
    comments: any[]
  ) => {
    setAnchorElFile(event.currentTarget);
    setCurrentCommentsForSelectedFile(comments);
  };

  const extendTableRecordDetails: ExtendedRepositoryObject[] =
    tableViewFiles.map((object: RepositoryObject) => {
      return {
        ...object,
        // Name is swap to absolute path because name is what we show on the table data grid
        absolutePath: object.name,
        name: object.name.replace(currentSelectedPath, "").replace("/", "")
      };
    });

  const { hideObjectsMutation, errorMutationHide, isLoadingMutationHide } =
    useHideObjectsMutation();

  const openHideModal = () => {
    setIsFileContextMenuOpen(false);
    setIsHideModalOpen(true);
    setAnchorElFile(null);
    setAnchorElFolder(null);
  };

  const openCompressModal = () => {
    setIsFileContextMenuOpen(false);
    setIsCompressModalOpen(true);
    setAnchorElFile(null);
    setAnchorElFolder(null);
  };

  const openUncompressModal = () => {
    setIsFileContextMenuOpen(false);
    setIsUncompressModalOpen(true);
    setAnchorElFile(null);
    setAnchorElFolder(null);
  };

  const openRenameModal = () => {
    setIsFileContextMenuOpen(false);
    setIsRenameModalOpen(true);
    setAnchorElFile(null);
    setAnchorElFolder(null);
  };

  const openCopyMoveModal = (typeOfAction: string) => {
    setIsFileContextMenuOpen(false);
    setIsCopyMoveModalOpen(true);
    setCopyOrMoveAction(typeOfAction);
    setAnchorElFile(null);
    setAnchorElFolder(null);
  };

  function hideObjects() {
    setIsFileContextMenuOpen(false);
    hideObjectsMutation({
      variables: {
        absolutePaths:
          selectedRows.length > 0 ? selectedRows : [tableMenuWasOpenOnPath],
        activityId: activityData.id,
        comment: hideComment
      }
    }).then(() => {
      refetchRepositoryObjects();
      closeHideModal();
    });
  }

  const onCloseExternalDownloadModal = () => {
    setIsDownloadExternalLinkVisible(false);
    setTableMenuWasOpenOnPath("");
  };

  const getRowId = (row: any) => row.absolutePath + row.versionId;

  return {
    getDownloadLinkForFiles,
    error: error,
    downloadUrls,
    selectedRows,
    downloadMultipleFiles,
    handleSelectMultipleRows,
    anchorElFolder,
    handleCloseFolder,
    anchorElFile,
    currentCommentsForSelectedFile,
    handleCloseCommentMenu,
    isFileContextMenuOpen,
    handleCloseFileMenu,
    handleOpenProperties,
    handleOpenTracebilityReport,
    folderName,
    setFolderName,
    isFolderNameError,
    setIsFolderNameError,
    createFolder,
    setCurrentCommentsForSelectedFile,
    getRowId,
    hideObjects,
    isHideModalOpen,
    setIsHideModalOpen,
    closeHideModal,
    hideComment,
    setHideComment,
    openHideModal,
    isRenameModalOpen,
    setIsRenameModalOpen,
    openRenameModal,
    handleClickFile,
    handleClickComment,
    handleClickFolder,
    handleOpenEditAction,
    setIsCopyMoveModalOpen,
    isCopyMoveModalOpen,
    openCopyMoveModal,
    copyOrMoveAction,
    handleOpenExternalDownloadLink,
    isDownloadExternalLinkVisible,
    onCloseExternalDownloadModal,
    setIsDownloadExternalLinkVisible,
    isCompressModalOpen,
    setIsCompressModalOpen,
    isUncompressModalOpen,
    setIsUncompressModalOpen,
    openUncompressModal,
    openCompressModal,
    setSelectedRows,
    errorMutationHide,
    isLoadingMutationHide,
    extendTableRecordDetails, // mutated data to separate aboslute path and name
    page,
    setPage,
    pageSize,
    setPageSize,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel
  };
}
